import React, { useEffect, useRef, useMemo } from "react";
import * as echarts from "echarts";
import toast from "react-hot-toast";
import FormCard from "../Components/Forms/FormCard";
import { chartColorPalette } from "../utils/utils";

const ForecastChart = ({
    forecastData = {},
    selectedAnomaly,
    tagOrder = [],
}) => {
    const chartRef = useRef(null);

    // Prepare chart data from new forecast data format
    const chartData = useMemo(() => {
        if (!forecastData || !forecastData.tag_aliases || !Array.isArray(forecastData.tag_aliases) || forecastData.tag_aliases.length === 0) {
            return { keys: [], seriesData: {}, timestamps: [], anomaliesData: {} };
        }

        const sourceKeys = forecastData.tag_aliases;
        const timestamps = forecastData.timestamps || [];
        const values = forecastData.values || {};
        const anomalies = forecastData.anomalies || {};
        const preferredOrder = Array.isArray(tagOrder) ? tagOrder : [];
        const keys = [
            ...preferredOrder.filter((tag) => sourceKeys.includes(tag)),
            ...sourceKeys.filter((tag) => !preferredOrder.includes(tag)),
        ];

        // Create series data for each tag
        const seriesData = {};
        const anomaliesData = {};

        keys.forEach(tag => {
            seriesData[tag] = values[tag] || [];
            // Map anomaly flags: "0" -> null, "1" -> actual value for red dot display
            anomaliesData[tag] = (anomalies[tag] || []).map((flag, idx) =>
                flag === "1" || flag === 1 ? (values[tag] || [])[idx] : null
            );
        });

        return { keys, seriesData, timestamps, anomaliesData };
    }, [forecastData, tagOrder]);

    // Show toast notification when forecast data is empty
    useEffect(() => {
        if (forecastData && Array.isArray(forecastData.tag_aliases) && forecastData.tag_aliases.length === 0) {
            toast.error("No forecast data available");
        }
    }, [forecastData]);

    useEffect(() => {
        // Don't initialize chart if there's no data
        if (!chartData.keys || chartData.keys.length === 0) {
            return;
        }

        const createNewChart = () => {
            const anomalyLegendData = chartData.keys.map((key) => `Ano-${key}`);

            const legendData = [
                ...chartData.keys,
                ...anomalyLegendData,
            ];
            const legendSelected = legendData.reduce((acc, key) => {
                acc[key] = true;
                return acc;
            }, {});

            const chartOption = {
                title: {
                    text: "Forecast Data",
                },
                tooltip: {
                    trigger: "axis",
                    axisPointer: {
                        type: "cross",
                    },
                },
                grid: {
                    left: "11%",
                    right: "11%",
                    top: 80,
                    containLabel: true,
                },
                toolbox: {
                    feature: {
                        dataView: { show: true, readOnly: false },
                        restore: { show: true },
                        dataZoom: {
                            yAxisIndex: "none",
                        },
                        saveAsImage: {},
                    },
                },
                legend: {
                    data: legendData,
                    selected: legendSelected,
                    orient: "horizontal",
                    align: "auto",
                    top: 35,
                    itemGap: 12,
                    type: "scroll",
                    formatter: function (name) {
                        return name;
                    },
                    selectedMode: "multiple",
                },
                xAxis: {
                    type: "category",
                    boundaryGap: false,
                    data: chartData.timestamps || [],
                },
                yAxis: [],
                dataZoom: [
                    {
                        type: "slider",
                    },
                    {
                        type: "inside",
                    },
                ],
                series: [],
            };

            chartData.keys.forEach((key, index) => {
                const seriesData = chartData.seriesData[key];
                const anomalyData = chartData.anomaliesData[key];

                // Main series with explicit color
                chartOption.series.push({
                    name: key,
                    type: "line",
                    yAxisIndex: index,
                    symbol: "none",
                    sampling: "lttb",
                    data: seriesData,
                    legendHoverLink: true,
                    lineStyle: { color: chartColorPalette[index % chartColorPalette.length] },
                    itemStyle: { color: chartColorPalette[index % chartColorPalette.length] },
                });

                // Anomaly series, always red
                chartOption.series.push({
                    name: `Ano-${key}`,
                    type: "line",
                    yAxisIndex: index,
                    symbol: "circle",
                    sampling: "lttb",
                    data: anomalyData,
                    lineStyle: {
                        type: "dotted",
                        color: "red",
                    },
                    itemStyle: {
                        color: "red",
                    },
                });

                chartOption.yAxis.push({
                    type: "value",
                    name: `${key}`,
                    position: index % 2 === 0 ? "left" : "right",
                    offset: Math.floor(index / 2) * 60,
                    alignTicks: true,
                    nameLocation: "middle",
                    nameGap: 32,
                    nameRotate: 90,
                    axisLine: {
                        show: true,
                    },
                    axisLabel: {
                        formatter: "{value}",
                        rotate: 45,
                    },
                    containLabel: true,
                });
            });

            return chartOption;
        };

        const chartInstance = echarts.init(chartRef.current);
        const chartOption = createNewChart();
        chartInstance.setOption(chartOption);

        const handleResize = () => {
            chartInstance.resize();
        };
        window.addEventListener("resize", handleResize);

        // Highlight and zoom to selected anomaly if present
        if (
            selectedAnomaly &&
            selectedAnomaly.DEP_TAG_ALIAS &&
            selectedAnomaly.TIMESTAMP
        ) {
            const tagIndex = chartData.keys.indexOf(selectedAnomaly.DEP_TAG_ALIAS);
            const timeIndex = chartData.timestamps.indexOf(selectedAnomaly.TIMESTAMP);
            if (tagIndex !== -1 && timeIndex !== -1) {
                chartInstance.dispatchAction({
                    type: "highlight",
                    seriesIndex: tagIndex * 2,
                    dataIndex: timeIndex,
                });
                const windowSize = 10;
                let start = Math.max(0, timeIndex - Math.floor(windowSize / 2));
                let end = Math.min(
                    chartData.timestamps.length - 1,
                    start + windowSize - 1
                );
                start = Math.max(0, end - windowSize + 1);
                chartInstance.dispatchAction({
                    type: "dataZoom",
                    startValue: chartData.timestamps[start],
                    endValue: chartData.timestamps[end],
                });
                setTimeout(() => {
                    chartInstance.dispatchAction({
                        type: "showTip",
                        seriesIndex: tagIndex * 2,
                        dataIndex: timeIndex,
                    });
                }, 200);
            }
        }

        return () => {
            window.removeEventListener("resize", handleResize);
            chartInstance.dispose();
        };
    }, [chartData, selectedAnomaly]);

    return (
        <FormCard>
            {!chartData.keys || chartData.keys.length === 0 ? (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px',
                    color: '#666',
                    fontSize: '16px'
                }}>
                    No forecast data available to display
                </div>
            ) : (
                <div
                    ref={chartRef}
                    style={{ height: "100%", minHeight: "400px", width: "100%" }}
                />
            )}
        </FormCard>
    );
};

export default ForecastChart;
